import { useState } from "react";
import type { SortKey } from "@/utils/media";

interface FilterPanelProps {
  genreOptions: string[];
  regionOptions: string[];
  yearOptions: number[];
  selectedGenres: string[];
  selectedRegions: string[];
  selectedYears: number[];
  sort: SortKey;
  onToggleGenre: (value: string) => void;
  onToggleRegion: (value: string) => void;
  onToggleYear: (value: number) => void;
  onSortChange: (value: SortKey) => void;
  onReset: () => void;
  activeCount: number;
}

const sortOptions: { value: SortKey; label: string; icon: string }[] = [
  { value: "published", label: "发布时间", icon: "ri-time-line" },
  { value: "rating", label: "评分优先", icon: "ri-star-line" },
  { value: "popularity", label: "热门程度", icon: "ri-fire-line" },
];

interface ChipGroupProps {
  title: string;
  children: React.ReactNode;
}

function ChipGroup({ title, children }: ChipGroupProps) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
      <span className="shrink-0 pt-1.5 text-sm font-semibold text-foreground-700 sm:w-16">
        {title}
      </span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function chipClass(active: boolean) {
  return [
    "cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors",
    active
      ? "bg-primary-500 text-background-50"
      : "bg-background-100 text-foreground-700 hover:bg-background-200",
  ].join(" ");
}

export default function FilterPanel({
  genreOptions,
  regionOptions,
  yearOptions,
  selectedGenres,
  selectedRegions,
  selectedYears,
  sort,
  onToggleGenre,
  onToggleRegion,
  onToggleYear,
  onSortChange,
  onReset,
  activeCount,
}: FilterPanelProps) {
  const [collapsed, setCollapsed] = useState(true);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-600" aria-hidden="true"></i>
          <span className="font-heading text-sm font-bold text-foreground-900">筛选与排序</span>
          {activeCount > 0 && (
            <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs font-semibold text-accent-800">
              已选 {activeCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {activeCount > 0 && (
            <button
              type="button"
              onClick={onReset}
              className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600 hover:text-accent-600"
            >
              重置筛选
            </button>
          )}
          <button
            type="button"
            onClick={() => setCollapsed((prev) => !prev)}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-sm text-foreground-700 md:hidden"
          >
            {collapsed ? "展开" : "收起"}
            <i className={collapsed ? "ri-arrow-down-s-line" : "ri-arrow-up-s-line"} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <div className={`mt-4 space-y-4 ${collapsed ? "hidden md:block" : "block"}`}>
        <ChipGroup title="题材">
          {genreOptions.map((genre) => (
            <button
              key={genre}
              type="button"
              onClick={() => onToggleGenre(genre)}
              className={chipClass(selectedGenres.includes(genre))}
            >
              {genre}
            </button>
          ))}
        </ChipGroup>

        <ChipGroup title="地区">
          {regionOptions.map((region) => (
            <button
              key={region}
              type="button"
              onClick={() => onToggleRegion(region)}
              className={chipClass(selectedRegions.includes(region))}
            >
              {region}
            </button>
          ))}
        </ChipGroup>

        <ChipGroup title="年份">
          {yearOptions.map((year) => (
            <button
              key={year}
              type="button"
              onClick={() => onToggleYear(year)}
              className={chipClass(selectedYears.includes(year))}
            >
              {year}
            </button>
          ))}
        </ChipGroup>

        <div className="border-t border-background-200 pt-4">
          <ChipGroup title="排序">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onSortChange(option.value)}
                className={`flex items-center gap-1.5 ${chipClass(sort === option.value)}`}
              >
                <i className={option.icon} aria-hidden="true"></i>
                {option.label}
              </button>
            ))}
          </ChipGroup>
        </div>
      </div>
    </div>
  );
}